// @flow
import qs from "qs";
import appConfig from "appConfig";
import consts from "common/consts";
import { getLogger } from "@cld/upload-widget";
import { sessionStorage, localStorage } from "utils/domUtils";

const FALLBACK_INTERVAL = 500; // every half a second
const FALLBACK_MAX_RETRIES = 360; // 500 * 2 * 180 = 3 minutes

const logger = getLogger();

const getTokenStorageKey = (cloudName: string, service: string) => `uw_${cloudName}_${service}_token`;

const getStoredAuthToken = (cloudName: string, service: string): ?string => sessionStorage.getItem(getTokenStorageKey(cloudName, service));

const removeAuthToken = (cloudName: string, service: string) => sessionStorage.removeItem(getTokenStorageKey(cloudName, service));

export const extractAuthToken = (data: string) => {
	const dataParams: Object = qs.parse(data.replace(/[?|#]/, ""), {
		decoder: (str, defaultDecoder, charset, type) => {
			return (type === "value") ? defaultDecoder(str).replace(/ /g, "+") : defaultDecoder(str);
	}});
	return dataParams.access_token || dataParams.token;
};

const setMessageAuthListener = (getAuthWin: Function, cloudName: string, service: string): Promise<{ token: string }> => new Promise((resolve) => {
	let resolved = false;
	let fallbackTimedListenerId = null;
	let counter = 0;

	localStorage.removeItem(consts.OAUTH_STORAGE_TOKEN); // make sure we start with a clean storage

	const resolveWithToken = (data: string) => {
		if (!resolved) {
			logger.log("[services]: received valid auth post message with data: ", data);

			const authToken = extractAuthToken(data);

			sessionStorage.setItem(getTokenStorageKey(cloudName, service), authToken);

			// clean up
			localStorage.removeItem(consts.OAUTH_STORAGE_TOKEN);
			self.removeEventListener("message", onMessageHandler);
			self.removeEventListener("storage", onStorageHandler);

			const authWin = getAuthWin();

			if (authWin) {
				authWin.close();
			}

			if (fallbackTimedListenerId) {
				clearInterval(fallbackTimedListenerId);
			}

			resolved = true;
			resolve({ token: authToken });
		}
	};

	const onMessageHandler = (e) => {
		const authWin = getAuthWin();

		if (authWin) {
			const { origin, source, data } = e;

			if (source === authWin
                    && appConfig.api.widgetAuth.startsWith(origin)) {
				resolveWithToken(data);
			}
		}
	};

	const onStorageHandler = (e) => { // failover #1 - should be called when auth window sets the hash to local storage
		if (e.key === consts.OAUTH_STORAGE_TOKEN && e.newValue) {
			resolveWithToken(e.newValue);
		}
	};

	self.addEventListener("message", onMessageHandler);
	self.addEventListener("storage", onStorageHandler);

	fallbackTimedListenerId = setInterval(() => { // failover #2 - wait 3 minutes for the hash to be added to local storage
		if (counter < FALLBACK_MAX_RETRIES) {
			const storageHash = localStorage.getItem(consts.OAUTH_STORAGE_TOKEN);

			if (storageHash) {
				resolveWithToken(storageHash);
			} else {
				counter += 1;
			}
		} else if (fallbackTimedListenerId) {
			clearInterval(fallbackTimedListenerId);
		}
	}, FALLBACK_INTERVAL);
});

const getAuthRedirectUrl = (newTlsDomain) => newTlsDomain ? appConfig.api.widgetAuthNewTls : appConfig.api.widgetAuth;

const postAuthResult = () => { // this is inside the auth popup
	const loc = self.location;

	// oauth services return the token either in the hash or search(query) - hash can sometimes have a _ so need to check for length
	const params = loc.hash && loc.hash.length > 2 ? loc.hash : loc.search;

	self.opener.postMessage(params, `${loc.protocol}//${loc.host}`);
};

export {
	getStoredAuthToken,
	setMessageAuthListener,
	removeAuthToken,
	getAuthRedirectUrl,
	postAuthResult,
};
